import { useCallback, useState } from "react"

import FilePicker from "../components/IntegrationFilePicker"
import type { CredentialMetadata } from "../credentials/providerTypes"
import useErrorPopup from "../hooks/useErrorPopup"
import { removeSource, updateOrCreateSource } from "../sources/api"
import type { ConfluenceSource, NotionSource } from "../sources/types"
import IntegrationBlock from "./IntegrationBlock"

interface Props {
  name: string
  source: NotionSource | ConfluenceSource | undefined
  sourceKind: "NOTION" | "CONFLUENCE"
  loading: boolean
  logo: string
  credential: CredentialMetadata | undefined
  connectCredential: () => Promise<void>
  disconnectCredential: () => Promise<void>
  notEnabled?: boolean
}

const FilePickerIntegrationBlock: React.FC<Props> = ({
  name,
  sourceKind,
  source,
  loading,
  logo,
  credential,
  connectCredential,
  disconnectCredential,
  notEnabled,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [processing, setProcessing] = useState(false)

  const { handleSuccess, handleError } = useErrorPopup()

  const onEdit = useCallback(() => {
    setPickerOpen(true)
  }, [])

  const missingCredential = !credential
  const missingSource = !source

  const connectSource = useCallback(
    async (forceReconnect = false) => {
      setProcessing(true)
      try {
        if (missingCredential || forceReconnect) {
          await connectCredential()
        }
        if (missingSource) {
          await updateOrCreateSource({
            kind: sourceKind,
            source_meta: {},
          })
        }
        onEdit()
      } catch (error) {
        handleError({ error, prefix: "Failed to connect integration" })
      } finally {
        setProcessing(false)
      }
    },
    [
      connectCredential,
      missingSource,
      sourceKind,
      missingCredential,
      onEdit,
      handleError,
    ],
  )

  const hidePicker = useCallback(() => {
    setPickerOpen(false)
  }, [])

  const onDisconnect = useCallback(async () => {
    setProcessing(true)
    try {
      if (!missingSource) {
        await removeSource(source.oid)
      }
      if (!missingCredential) {
        await disconnectCredential()
      }
      handleSuccess(
        "Successfully disconnected. Your documents will be removed from Quilt in the background",
      )
    } catch (error) {
      handleError({ error, prefix: "Failed to disconnect integration" })
    } finally {
      setProcessing(false)
    }
  }, [
    source?.oid,
    missingSource,
    missingCredential,
    disconnectCredential,
    handleSuccess,
    handleError,
  ])

  return (
    <>
      <FilePicker
        open={pickerOpen}
        onCancel={hidePicker}
        source={source}
        isModal
      />
      <IntegrationBlock
        loading={loading || processing}
        name={name}
        connected={!missingCredential && !missingSource}
        onEdit={onEdit}
        onConnect={connectSource}
        onReconnect={() => connectSource(true)}
        onDisconnect={onDisconnect}
        logo={logo}
        notEnabled={notEnabled}
        disabledReason={
          source?.removal_requested
            ? "Removal requested"
            : pickerOpen
              ? "Use the form above to modify the source"
              : undefined
        }
      />
    </>
  )
}
export default FilePickerIntegrationBlock
